/**
 * Scene API Client
 * Fetches scene documents from the main server.
 * Uses IndexedDB cache to avoid re-downloading unchanged scenes.
 */

import { serverConfig } from '../network/ServerConfig';
import { getCachedScene, cacheScene } from './sceneCache';
import type { SDLSceneDocument, GlobalGameConfig, GameModeId } from '@sdl/scene-format';

export interface SceneApiError {
  message: string;
  status?: number;
}

interface SceneMeta {
  sceneId: string;
  name: string;
  revision: number;
  updatedAt: string;
}

/**
 * Fetch scene metadata (lightweight freshness check)
 */
async function fetchMeta(sceneId: string): Promise<SceneMeta> {
  const url = `${serverConfig.mainServerUrl}/api/scenes/${sceneId}/meta`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch scene meta: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch full scene document from server
 */
async function fetchScene(url: string): Promise<SDLSceneDocument> {
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error: SceneApiError = {
      message: `Failed to fetch scene: ${response.statusText}`,
      status: response.status,
    };
    throw error;
  }

  return response.json() as Promise<SDLSceneDocument>;
}

/**
 * Load a scene with cache-aware logic:
 * 1. Check IndexedDB cache
 * 2. If cached, compare revision via /meta endpoint
 * 3. Return cached if fresh, otherwise re-download
 * 4. If server unreachable, return cached as offline fallback
 */
async function getWithCache(sceneId: string, fetchUrl: string): Promise<SDLSceneDocument> {
  const cached = await getCachedScene(sceneId);

  if (cached) {
    // Check freshness via revision number
    try {
      const meta = await fetchMeta(sceneId);
      if (cached.revision >= meta.revision) {
        console.log(`📦 Scene "${cached.document.metadata?.name || sceneId}" loaded from cache (rev ${cached.revision})`);
        return cached.document;
      }
      console.log(`📦 Scene "${sceneId}" cache stale (rev ${cached.revision} < ${meta.revision}), re-downloading`);
    } catch {
      // Server unreachable — return cached as offline fallback
      console.log(`📦 Scene "${cached.document.metadata?.name || sceneId}" loaded from cache (offline)`);
      return cached.document;
    }
  }

  // Fetch fresh from server
  const doc = await fetchScene(fetchUrl);

  // Cache the result with revision
  const id = doc.metadata?.id || sceneId;
  try {
    const meta = await fetchMeta(id).catch(() => null);
    await cacheScene(id, meta?.revision ?? 1, doc);
  } catch {
    // Cache write failure is non-critical
  }

  return doc;
}

export const sceneApi = {
  /**
   * Fetch the default scene — uses cache when available
   */
  async getDefault(): Promise<SDLSceneDocument> {
    // First try: get default scene ID from list to enable caching
    try {
      const listUrl = `${serverConfig.mainServerUrl}/api/scenes?isDefault=true`;
      const listResponse = await fetch(listUrl);
      if (listResponse.ok) {
        const scenes = await listResponse.json();
        const defaultScene = scenes.find((s: any) => s.isDefault);
        if (defaultScene) {
          return getWithCache(
            defaultScene.sceneId,
            `${serverConfig.mainServerUrl}/api/scenes/${defaultScene.sceneId}`
          );
        }
      }
    } catch {
      // Fall through to direct fetch
    }

    // Fallback: fetch directly (no caching for /default since we don't know the ID)
    return fetchScene(`${serverConfig.mainServerUrl}/api/scenes/default`);
  },

  /**
   * Fetch a scene by ID — uses cache when available
   */
  async getById(sceneId: string): Promise<SDLSceneDocument> {
    return getWithCache(sceneId, `${serverConfig.mainServerUrl}/api/scenes/${sceneId}`);
  },

  /**
   * Fetch the scene for a specific game mode.
   * Looks up the game mode config, finds assigned scenes, and loads the first one.
   * Falls back to the default scene if no scene is assigned.
   */
  async getForGameMode(gameModeId: GameModeId): Promise<SDLSceneDocument> {
    try {
      const configUrl = `${serverConfig.mainServerUrl}/api/config/game-modes`;
      const response = await fetch(configUrl);
      if (response.ok) {
        const config: GlobalGameConfig = await response.json();
        const modeConfig = config.gameModes.find(m => m.id === gameModeId);
        if (modeConfig && modeConfig.sceneIds.length > 0) {
          const sceneId = modeConfig.selectionMode === 'random'
            ? modeConfig.sceneIds[Math.floor(Math.random() * modeConfig.sceneIds.length)]
            : modeConfig.sceneIds[0];
          console.log(`🎬 Loading scene "${sceneId}" for game mode "${gameModeId}"`);
          return this.getById(sceneId);
        }
      }
    } catch {
      // Fall through to default
    }
    console.log(`🎬 No scene configured for mode "${gameModeId}", using default`);
    return this.getDefault();
  },
};
